// What is being written in a post box, shared by the person's devices
// (`/api/me/drafts/:key`) and kept on this device too, so moving to another
// chat, reloading, going offline or the server being out of reach never loses it.
//
// Promise:
// - The key names the box (a chat, a new chat, a side chat not made yet, a
//   conversation). A new key reads that box's draft; nothing of the previous
//   box is carried.
// - Typing is saved about a second after it stops (not on every key); emptying
//   the box (sending empties it) removes the draft at once. The attached files
//   that are up are saved with the text, in their order.
// - What is written is first kept on this device. Until the server has it, it
//   stays marked as not sent and goes again when the box opens again, when
//   the browser is back online, or with the next change.
// - When another device changes the draft (`draft.changed`), a box with no
//   change of its own waiting to be saved takes the new text and files. A box
//   being typed in keeps what is typed; once it is saved, that becomes every
//   device's draft (the later typing stays). Another page of this device
//   writing the same box is taken in the same way at once (the page that
//   wrote it saves it).
// - A box whose text is handed over to what removes the draft (an answer)
//   forgets it: nothing waiting is saved, nothing is kept on this device.
// - Where storage cannot be written (private mode, full), writing goes on.
// - Keys follow the old web's (`trameria-draft:topic:<id>`); a plain text kept
//   there by the old web is read as a change of this device not yet sent.
import {
  type QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../app/context";
import type { Api } from "../data/api";

/** A file already up, as a draft keeps it. */
export interface RestoredFile {
  id: string;
  name: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
}

/** What the draft needs of a box's attached files (agent-chat's `Uploads`). */
export interface DraftUploads {
  items: readonly {
    fileId: string | null;
    name: string;
    mime: string;
    size: number;
    width: number | null;
    height: number | null;
  }[];
  /** A file still going up. */
  busy: boolean;
  restore(files: readonly RestoredFile[]): void;
}

const PREFIX = "trameria-draft:";
const SAVE_AFTER_MS = 1000;

export interface ServerDraft {
  key: string;
  body: string;
  files: RestoredFile[];
  updatedAt: string;
}

interface Local {
  body: string;
  files: RestoredFile[];
  /** Changed here and not yet saved on the server. */
  dirty: boolean;
}

const EMPTY: Local = { body: "", files: [], dirty: false };

function readLocal(key: string | null): Local {
  if (key === null) return EMPTY;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PREFIX + key);
  } catch {
    return EMPTY;
  }
  if (raw === null) return EMPTY;
  if (raw.startsWith('{"v":1')) {
    try {
      const v = JSON.parse(raw) as { b?: string; f?: RestoredFile[]; d?: 1 };
      return {
        body: typeof v.b === "string" ? v.b : "",
        files: Array.isArray(v.f) ? v.f : [],
        dirty: v.d === 1,
      };
    } catch {
      return EMPTY;
    }
  }
  // The old web's plain text: written here, never sent.
  return { body: raw, files: [], dirty: raw !== "" };
}

function writeLocal(key: string | null, local: Local) {
  if (key === null) return;
  try {
    if (local.body === "" && local.files.length === 0 && !local.dirty)
      localStorage.removeItem(PREFIX + key);
    else
      localStorage.setItem(
        PREFIX + key,
        JSON.stringify({
          v: 1,
          b: local.body,
          f: local.files,
          ...(local.dirty ? { d: 1 } : {}),
        }),
      );
  } catch {
    // Kept in this screen only.
  }
}

/** The draft key of a chat's post box. */
export const chatDraftKey = (chatId: string) => `topic:${chatId}`;

export const draftsPrefix = ["me", "drafts"] as const;
export const draftKey = (key: string) => [...draftsPrefix, key] as const;

export const draftQuery = (api: Api, key: string) =>
  queryOptions({
    queryKey: draftKey(key),
    queryFn: () =>
      api<{ draft: ServerDraft | null }>(
        `/api/me/drafts/${encodeURIComponent(key)}`,
      ).then((r) => r.draft),
  });

/** Another device (or this one) changed a draft: read it again. */
export function invalidateDraft(qc: QueryClient, key: string | undefined) {
  void qc.invalidateQueries({
    queryKey: key ? draftKey(key) : draftsPrefix,
  });
}

const sameFiles = (
  a: readonly { id: string }[],
  b: readonly { id: string }[],
) => a.length === b.length && a.every((f, i) => f.id === b[i].id);

const readyFiles = (uploads: DraftUploads | undefined): RestoredFile[] =>
  (uploads?.items ?? [])
    .filter((u) => u.fileId !== null)
    .map((u) => ({
      id: u.fileId as string,
      name: u.name,
      mime: u.mime,
      size: u.size,
      width: u.width,
      height: u.height,
    }));

/**
 * The text of a box and how to change it. With `uploads`, the box's files
 * that are up are kept with the text and put back from the draft. A null key
 * is no box (nothing is read or kept): a hook that is always called, for a
 * box that comes and goes (an item's answer).
 */
export function useDraft(
  key: string | null,
  uploads?: DraftUploads,
): [
  string,
  (next: string | ((current: string) => string)) => void,
  /**
   * What is written is being sent somewhere that removes the draft (an
   * answer): nothing waiting is saved and this device keeps no copy, while
   * the box keeps showing it. Writing again saves it again.
   */
  () => void,
] {
  const api = useApi();
  const qc = useQueryClient();
  const server = useQuery({
    ...draftQuery(api, key ?? ""),
    enabled: key !== null,
  });
  const [state, setState] = useState(() => ({ key, ...readLocal(key) }));
  // Another box: read its draft during render (no extra paint of the old one).
  let current = state;
  if (state.key !== key) {
    current = { key, ...readLocal(key) };
    setState(current);
  }
  const live = useRef(current);
  live.current = current;
  /** A save waiting (timer) or on its way: the box is being typed in. */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(0);
  /** Files put back from a draft, so taking them in is not a change of ours. */
  const restored = useRef<RestoredFile[] | null>(null);
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  /** The change waiting for the timer (it may be of the box just left). */
  const waiting = useRef<(Local & { key: string | null }) | null>(null);

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const sent = waiting.current ?? live.current;
    waiting.current = null;
    if (!sent.dirty || sent.key === null) return;
    const sentKey = sent.key;
    inFlight.current += 1;
    try {
      const path = `/api/me/drafts/${encodeURIComponent(sentKey)}`;
      let draft: ServerDraft | null = null;
      if (sent.body === "" && sent.files.length === 0)
        await api(path, { method: "DELETE" });
      else
        draft = (
          await api<{ draft: ServerDraft | null }>(path, {
            method: "PUT",
            body: { body: sent.body, fileIds: sent.files.map((f) => f.id) },
          })
        ).draft;
      // A read that left before this save would answer what was there before.
      await qc.cancelQueries({ queryKey: draftKey(sentKey) });
      qc.setQueryData(draftKey(sentKey), draft);
      const now = live.current;
      // Nothing typed meanwhile: what is here is what the server has.
      if (
        now.key === sent.key &&
        now.body === sent.body &&
        sameFiles(now.files, sent.files)
      ) {
        const clean = { ...now, dirty: false };
        live.current = clean;
        writeLocal(sent.key, clean);
        setState((s) => (s.key === sent.key ? clean : s));
      } else if (now.key !== sent.key) {
        const kept = readLocal(sent.key);
        if (kept.body === sent.body && sameFiles(kept.files, sent.files))
          writeLocal(sent.key, { ...kept, dirty: false });
      }
    } catch {
      // Kept here as not sent; it goes again (see the promise).
    } finally {
      inFlight.current -= 1;
    }
  }, [api, qc]);

  /** Shows a draft's files in the box (when they are not already there). */
  const putBack = useCallback((files: readonly RestoredFile[]) => {
    const box = uploadsRef.current;
    if (!box || sameFiles(readyFiles(box), files)) return;
    restored.current = [...files];
    box.restore(files);
  }, []);

  const change = useCallback(
    (patch: Partial<Pick<Local, "body" | "files">>) => {
      const next = { ...live.current, ...patch, dirty: true };
      live.current = next;
      writeLocal(next.key, next);
      setState(next);
      if (timer.current) clearTimeout(timer.current);
      waiting.current = next;
      if (next.body === "" && next.files.length === 0) void flush();
      else timer.current = setTimeout(() => void flush(), SAVE_AFTER_MS);
    },
    [flush],
  );

  // The box opens: files kept for it come back, and what was not sent goes.
  // frontend-development: not a fetch; puts back what this device kept and sends what it could not.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs again for each box (key)
  useEffect(() => {
    const local = live.current;
    putBack(local.files);
    if (local.dirty) void flush();
    const online = () => void flush();
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("online", online);
      // Leaving the box: what is waiting goes now.
      if (timer.current) void flush();
    };
  }, [key, flush, putBack]);

  // The files that are up changed by the person (added, taken off, done going up).
  const ready = readyFiles(uploads);
  const readyIds = ready.map((f) => f.id).join(",");
  // frontend-development: not a fetch; follows the box's attached files into the draft.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the files that are up change (readyIds)
  useEffect(() => {
    if (!uploadsRef.current) return;
    const files = readyFiles(uploadsRef.current);
    if (restored.current) {
      // Until the put back files are in, the box's files are not the person's.
      if (sameFiles(files, restored.current)) restored.current = null;
      return;
    }
    if (sameFiles(files, live.current.files)) return;
    change({ files });
  }, [readyIds, change]);

  // Another page of this device wrote the same box (another tab, or the
  // app's other web view, as an item's UI page reading the comment written
  // in the screens' box): taken in at once, as another device's draft is,
  // and left to that page to save.
  // frontend-development: not a fetch; follows this device's storage written by another page.
  useEffect(() => {
    if (key === null) return;
    const heard = (e: StorageEvent) => {
      if (e.key !== PREFIX + key) return;
      const local = live.current;
      if (
        local.key !== key ||
        local.dirty ||
        timer.current ||
        inFlight.current > 0 ||
        uploadsRef.current?.busy
      )
        return;
      const theirs = readLocal(key);
      if (theirs.body === local.body && sameFiles(theirs.files, local.files))
        return;
      const next = {
        key,
        body: theirs.body,
        files: theirs.files,
        dirty: false,
      };
      live.current = next;
      setState(next);
      putBack(theirs.files);
    };
    window.addEventListener("storage", heard);
    return () => window.removeEventListener("storage", heard);
  }, [key, putBack]);

  // Another device's draft: taken in unless this box has a change waiting.
  const remote = server.data;
  // frontend-development: not a fetch; takes in the draft the query read.
  useEffect(() => {
    if (remote === undefined) return;
    const local = live.current;
    if (
      local.dirty ||
      timer.current ||
      inFlight.current > 0 ||
      uploadsRef.current?.busy
    )
      return;
    const body = remote?.body ?? "";
    const files = remote?.files ?? [];
    if (body === local.body && sameFiles(files, local.files)) return;
    const next = { key: local.key, body, files, dirty: false };
    live.current = next;
    writeLocal(next.key, next);
    setState(next);
    putBack(files);
  }, [remote, putBack]);

  const set = useCallback(
    (next: string | ((current: string) => string)) => {
      const body = typeof next === "function" ? next(live.current.body) : next;
      if (body !== live.current.body) change({ body });
    },
    [change],
  );
  const forget = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    waiting.current = null;
    const next = { ...live.current, dirty: false };
    live.current = next;
    try {
      if (next.key !== null) localStorage.removeItem(PREFIX + next.key);
    } catch {
      // Nothing was kept.
    }
    setState(next);
  }, []);
  return [current.body, set, forget];
}
